// Admin reads: one quiet second try before a failure is shown.
//
// A blip (the network dropped for a moment, a deploy's few seconds of 502s)
// should not put "Couldn't load" in front of the admin, so a read that fails
// that way is asked again once, about 1.5 s later. A read the server answered
// on purpose is never asked again: a 4xx says the same thing the second time
// (401 = the session ended and apiAdmin is already going to sign-in; 404 = the
// record or the route is not there).

// Mutable so a test can skip the wait; nothing in the app changes it.
export const ADMIN_RETRY = { delayMs: 1500 };

const statusOf = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } } | null)?.response?.status;

// No answer at all (offline, timeout, CORS on a crashed server), a 5xx, a
// request timeout or "too many requests".
export function isWorthRetrying(err: unknown): boolean {
  const s = statusOf(err);
  return s === undefined || s >= 500 || s === 408 || s === 429;
}

export const isNotFound = (err: unknown): boolean => statusOf(err) === 404;

export async function withQuietRetry<T>(read: () => Promise<T>): Promise<T> {
  try {
    return await read();
  } catch (err) {
    if (!isWorthRetrying(err)) throw err;
    await new Promise((resolve) => setTimeout(resolve, ADMIN_RETRY.delayMs));
    return read();
  }
}
